// ============================================================================
// PublicationCard — carte de publication du fil social (MASTER-GEST-E1A/E1B)
// ============================================================================
// Carte de fil : avatar auteur + nom + temps relatif + catégorie (texte discret) ·
// titre text-body semibold · corps + « Lire la suite » (lien brand — dérouler du
// CONTENU est du wayfinding) · pied = barre de réactions emoji + « Vu par N » +
// « N commentaires ». Barre de réactions INTERNE (décision E1a — la promotion
// GsReactionBar standalone est un chore ultérieur si réutilisée).
// E1b — INTERACTIVITÉ opt-in par props (rétro-compatible lecture E1a) :
//   - onReact : les réactions deviennent des BOUTONS toggle (aria-pressed,
//     état actif = teinte brand) — l'appelant porte la mutation optimiste ;
//   - onToggleComments : le libellé commentaires devient un bouton déplieur
//     (aria-expanded = commentsExpanded) ;
//   - children : zone dépliée sous le pied (liste de commentaires + saisie —
//     voir PublicationCommentInput), rendue par l'appelant.
// L'AUTORISATION ne vit JAMAIS ici : le serveur décide (bind-to-fetch), la
// carte ne fait que rendre les affordances que l'appelant lui donne.
// Data-driven, i18n par props (JAMAIS de t() dans @gs/ui) — l'appelant fournit
// les libellés déjà traduits/formatés (temps relatif, « Vu par N », etc.).
// Tokens slate-*/brand-* + échelle nommée uniquement (règle #28) — zéro hex.

import { useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import { GsAvatar } from './GsAvatar';

export interface PublicationCardReaction {
  /** Clé stable de la réaction (like | love | celebrate…). */
  key: string;
  /** Emoji affiché (👍 ❤️ 🎉). */
  emoji: string;
  /** Libellé traduit (« J'aime »). Omis en rendu compact si vide. */
  label: string;
  count: number;
  /** La réaction ACTIVE du viewer (résolue serveur — myReaction). E1b. */
  active?: boolean;
}

export interface PublicationCardProps {
  authorName: string;
  authorPhotoUrl?: string | null;
  /** Temps relatif déjà formaté (« il y a 14 jours »). */
  postedAtLabel: string;
  /** Catégorie déjà traduite (« Contrats ») — texte discret, pas un badge. */
  categoryLabel: string;
  title: string;
  body: string;
  /**
   * Image de la publication — **object URL** (`blob:`) DÉJÀ résolu par
   * l'appelant (CHORE-GEST-BLOBS : le contenu est servi par un proxy d'app sous
   * Bearer, un `<img src>` nu n'emporte pas le token → `useBlobObjectUrl`).
   * Absent/undefined = rien n'est rendu (chargement, blob manquant, storage non
   * câblé) : la carte reste valide sans image — jamais de cadre cassé.
   *
   * Nommé `imageObjectUrl` et NON `imageUrl` : le contrat est « on te passe un
   * object URL résolu », jamais un chemin/URL de storage à proxifier. (DS-002
   * `require-get-image-url` vise l'ancien pattern SAS `/api/image-proxy` +
   * `getImageUrl()` — il ne s'applique pas ici, et @gs/ui n'importe de toute
   * façon pas @gs/api-client, qui n'est qu'une peerDependency.)
   */
  imageObjectUrl?: string | null;
  /** Texte alternatif DÉJÀ traduit (A11y — jamais de t() dans @gs/ui). */
  imageAlt?: string;
  /** Libellé « Lire la suite » (obligatoire — apparaît si le corps déborde). */
  readMoreLabel: string;
  /** Libellé de repli optionnel (« Réduire ») — sans lui, pas de repli après expansion. */
  readLessLabel?: string;
  reactions?: PublicationCardReaction[];
  /** « Vu par 10 » déjà formaté — masqué si absent. */
  viewsLabel?: string;
  /** « 1 commentaire » déjà formaté — masqué si absent. */
  commentsLabel?: string;
  /** E1b : réactions cliquables (toggle) — sans lui, barre en lecture (E1a). */
  onReact?: (key: string) => void;
  /** E1b : le libellé commentaires devient un bouton déplieur. */
  onToggleComments?: () => void;
  /** État déplié de la zone commentaires (aria-expanded). */
  commentsExpanded?: boolean;
  /** E1b : zone dépliée (commentaires + saisie) rendue par l'appelant. */
  children?: ReactNode;
  className?: string;
}

/** Carte de publication du fil — lecture (E1a) + interactions opt-in (E1b). */
export function PublicationCard({
  authorName,
  authorPhotoUrl,
  postedAtLabel,
  categoryLabel,
  title,
  body,
  imageObjectUrl,
  imageAlt = '',
  readMoreLabel,
  readLessLabel,
  reactions = [],
  viewsLabel,
  commentsLabel,
  onReact,
  onToggleComments,
  commentsExpanded = false,
  children,
  className = '',
}: PublicationCardProps) {
  const bodyRef = useRef<HTMLParagraphElement>(null);
  const [expanded, setExpanded] = useState(false);
  const [overflowing, setOverflowing] = useState(false);

  // Le lien « Lire la suite » n'apparaît que si le corps clampé déborde réellement.
  useLayoutEffect(() => {
    const el = bodyRef.current;
    if (!el || expanded) return;
    setOverflowing(el.scrollHeight > el.clientHeight + 1);
  }, [body, expanded]);

  const hasFoot = reactions.length > 0 || viewsLabel || commentsLabel;

  const reactionContent = (r: PublicationCardReaction) => (
    <>
      <span aria-hidden>{r.emoji}</span>
      {r.label && <span>{r.label}</span>}
      {r.count > 0 && (
        <span
          className={`text-meta tabular-nums ${r.active ? 'text-brand-600 dark:text-brand-400' : 'text-slate-400 dark:text-slate-500'}`}
        >
          {r.count}
        </span>
      )}
    </>
  );

  return (
    <article
      className={`rounded-xl border border-slate-200 bg-white p-5 shadow-xs dark:border-slate-700 dark:bg-slate-800 ${className}`.trim()}
    >
      <header className="mb-2.5 flex items-center gap-2.5">
        <GsAvatar name={authorName} photoUrl={authorPhotoUrl} size="sm" />
        <div className="min-w-0 flex-1">
          <p className="truncate text-field font-semibold leading-tight text-slate-700 dark:text-slate-200">
            {authorName}
          </p>
          <p className="text-meta text-slate-400 dark:text-slate-500">{postedAtLabel}</p>
        </div>
        <span className="shrink-0 text-meta font-medium text-slate-400 dark:text-slate-500">
          {categoryLabel}
        </span>
      </header>

      <h3 className="mb-1.5 text-body font-semibold text-slate-800 dark:text-slate-100">{title}</h3>
      <p
        ref={bodyRef}
        className={`whitespace-pre-line text-body leading-relaxed text-slate-600 dark:text-slate-300 ${expanded ? '' : 'line-clamp-3'}`.trim()}
      >
        {body}
      </p>
      {(overflowing || (expanded && readLessLabel)) && (
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          className="mt-1 touch-manipulation text-body font-medium text-brand-600 transition-colors hover:text-brand-700 dark:text-brand-400 dark:hover:text-brand-300"
        >
          {expanded ? readLessLabel : readMoreLabel}
        </button>
      )}

      {/* Image du post — sous le corps, au-dessus du pied. `max-h` borne les
          formats portrait ; object-contain préserve le cadrage d'origine (une
          photo d'équipe recadrée en cover perdrait des visages). */}
      {imageObjectUrl && (
        <img
          src={imageObjectUrl}
          alt={imageAlt}
          className="mt-3 max-h-96 w-full rounded-lg border border-slate-100 bg-slate-50 object-contain dark:border-slate-700 dark:bg-slate-900"
        />
      )}

      {hasFoot && (
        <footer className="mt-3.5 flex flex-wrap items-center justify-between gap-x-4 gap-y-2 border-t border-slate-100 pt-3 dark:border-slate-700/60">
          <div className="flex flex-wrap items-center gap-x-2 gap-y-1.5">
            {reactions.map((r) =>
              onReact ? (
                <button
                  key={r.key}
                  type="button"
                  aria-pressed={!!r.active}
                  onClick={() => onReact(r.key)}
                  className={`inline-flex touch-manipulation items-center gap-1.5 rounded-full px-2.5 py-1 text-field transition-colors ${
                    r.active
                      ? 'bg-brand-500/10 font-medium text-brand-700 dark:text-brand-300'
                      : 'text-slate-600 hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-slate-700'
                  }`}
                >
                  {reactionContent(r)}
                </button>
              ) : (
                <span
                  key={r.key}
                  className="inline-flex items-center gap-1.5 px-2.5 py-1 text-field text-slate-600 dark:text-slate-300"
                >
                  {reactionContent(r)}
                </span>
              ),
            )}
          </div>
          {(viewsLabel || commentsLabel) && (
            <div className="flex items-center gap-3.5 text-meta text-slate-400 dark:text-slate-500">
              {viewsLabel && <span>{viewsLabel}</span>}
              {commentsLabel &&
                (onToggleComments ? (
                  <button
                    type="button"
                    aria-expanded={commentsExpanded}
                    onClick={onToggleComments}
                    className="touch-manipulation font-medium text-slate-500 transition-colors hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200"
                  >
                    {commentsLabel}
                  </button>
                ) : (
                  <span>{commentsLabel}</span>
                ))}
            </div>
          )}
        </footer>
      )}

      {children && (
        <div className="mt-3 border-t border-slate-100 pt-3 dark:border-slate-700/60">{children}</div>
      )}
    </article>
  );
}
